import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import { normalizeAssignedTrainers } from '../../details/trainerDetailsHelpers'
import { buildTrainingPayload } from '../../common/submitTraining'
import useTrainingsApi from '../../list/hooks/useTrainingsApi'

const mapCertificationOptions = items => (
	(Array.isArray(items) ? items : []).map(certification => ({
		value: certification._id,
		label: certification.name,
	}))
)

const mapTrainingNameOptions = items => (
	(Array.isArray(items) ? items : []).map(trainingName => ({
		value: trainingName.name,
		label: trainingName.name,
	}))
)

const buildPlacowkaSnapshot = data => ({
	_id: data?.placowkaId || '',
	id: data?.placowkaId || '',
	nazwa: data?.placowkaName || '',
	adres: data?.placowkaAddress || '',
	id_miasto: data?.id_miasto || null,
	id_gmina: data?.id_gmina || null,
	id_powiat: data?.id_powiat || null,
	id_wojewodztwo: data?.id_wojewodztwo || null,
	id_typ: data?.id_typ || null,
})

export default function useTrainingEditPage() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const { getAllCertifications, getAllTrainingCatalog, getContentOptions, getTrainingById, updateTraining } = useTrainingsApi()
	const [training, setTraining] = useState(null)
	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState(false)
	const [selectedPlacowka, setSelectedPlacowka] = useState(null)
	const [certificationOptions, setCertificationOptions] = useState([])
	const [trainingNameOptions, setTrainingNameOptions] = useState([])
	const [contentOptions, setContentOptions] = useState([])

	const handleCancel = useCallback(() => {
		navigate('/centrumSzkolen/szkolenia')
	}, [navigate])

	const handleClearPlacowka = useCallback(() => {
		setSelectedPlacowka(null)
	}, [])

	const loadTraining = useCallback(async () => {
		setLoading(true)
		const [trainingResult, certificationsResult, trainingCatalogResult, contentOptionsResult] = await Promise.all([
			getTrainingById(id),
			getAllCertifications({ limit: 999, sortColumn: 'name', sortDirection: 'asc' }),
			getAllTrainingCatalog({ limit: 999, sortColumn: 'name', sortDirection: 'asc' }),
			getContentOptions(),
		])

		const data = trainingResult?.data
		const error = trainingResult?.error

		if (error || !data) {
			await Swal.fire(
				t('trainings.edit.messages.errorTitle'),
				error || t('trainings.edit.messages.loadFailed'),
				'error'
			)
			navigate('/centrumSzkolen/szkolenia')
			return
		}

		if (certificationsResult.error || trainingCatalogResult.error || contentOptionsResult.error) {
			await Swal.fire(
				t('trainings.edit.messages.errorTitle'),
				certificationsResult.error || trainingCatalogResult.error || contentOptionsResult.error || t('trainings.edit.messages.loadFailed'),
				'error'
			)
			navigate('/centrumSzkolen/szkolenia')
			return
		}

		setCertificationOptions(mapCertificationOptions(certificationsResult.data))
		setTrainingNameOptions(mapTrainingNameOptions(trainingCatalogResult.data))
		setContentOptions(contentOptionsResult.data)
		setTraining(data)
		setSelectedPlacowka(buildPlacowkaSnapshot(data))
		setLoading(false)
	}, [getAllCertifications, getAllTrainingCatalog, getContentOptions, getTrainingById, id, navigate, t])

	useEffect(() => {
		if (id) loadTraining()
	}, [id, loadTraining])

	const handleSubmit = useCallback(async form => {
		if (submitting) return

		if (!selectedPlacowka) {
			await Swal.fire({
				icon: 'error',
				title: t('trainings.edit.messages.errorTitle'),
				text: t('trainings.edit.messages.placowkaRequired'),
			})
			return
		}

		setSubmitting(true)

		const payload = {
			...buildTrainingPayload(form, selectedPlacowka),
			assignedTrainers: normalizeAssignedTrainers(training),
		}
		const result = await updateTraining(id, payload)

		if (!result.success) {
			await Swal.fire(
				t('trainings.edit.messages.errorTitle'),
				result.error || t('trainings.edit.messages.updateFailed'),
				'error'
			)
			setSubmitting(false)
			return
		}

		setSubmitting(false)
		navigate(`/centrumSzkolen/szkolenia/details/${id}`)
	}, [id, navigate, selectedPlacowka, submitting, t, training, updateTraining])

	return {
		certificationOptions,
		contentOptions,
		handleCancel,
		handleClearPlacowka,
		handleSubmit,
		loading,
		selectedPlacowka,
		setSelectedPlacowka,
		submitting,
		training,
		trainingNameOptions,
	}
}
